import { useEffect, useRef, useState } from 'react';
import type { Animation } from '@adflow/types';
import { getAnimationEndTime } from '@adflow/utils';

// Keep the held end state visible for a beat before snapping back to the
// static design, so e.g. a Fade Out doesn't pop back the instant it finishes.
const END_HOLD_SECONDS = 0.5;

export type AnimationPlayback = {
  /** Seconds into the playback run, or null when idle (static design). */
  currentTime: number | null;
  isPlaying: boolean;
};

/**
 * Plays the animation once in the preview, advancing in sync with the browser
 * paint cycle. Each change of `replayToken` (to a value > 0) starts from t=0
 * up to the last keyframe, then the preview returns to the static design.
 */
export function useAnimationPlayback(
  animation: Animation | null | undefined,
  replayToken: number,
): AnimationPlayback {
  const [currentTime, setCurrentTime] = useState<number | null>(null);

  // Snapshot the animation at replay time without restarting playback (and
  // re-triggering it) whenever the animation object is edited mid-run.
  const animationRef = useRef(animation);
  useEffect(() => {
    animationRef.current = animation;
  }, [animation]);

  useEffect(() => {
    if (replayToken <= 0 || !animationRef.current) {
      return;
    }
    const endTime = getAnimationEndTime(animationRef.current);
    if (endTime <= 0) {
      return;
    }

    let frameId: number;
    const startedAt = performance.now();
    setCurrentTime(0);

    const tick = (now: number) => {
      const elapsed = (now - startedAt) / 1000;
      if (elapsed >= endTime + END_HOLD_SECONDS) {
        setCurrentTime(null);
        return;
      }
      // Clamp so the held end state is rendered exactly during the hold.
      setCurrentTime(Math.min(elapsed, endTime));
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameId);
      setCurrentTime(null);
    };
  }, [replayToken]);

  return { currentTime, isPlaying: currentTime !== null };
}
